(ns web3.screens.root
  (:require [re-frame-simple.db :as db]
            [web3.hints.core :as hint]
            [web3.ui-state :as ui-state]
            [web3.components.top-nav :as top-nav]
            [web3.components.snackbar :as snackbar]
            [web3.routing.core :as routing]
            [web3.view :as v]
            [web3.constants :as constants]
            [web3.i18n :as i]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [web3.utils :as utils]
            [web3.components.intercom :as intercom]
            [web3.modals.alerts :as alerts]
            [web3.ui-state :as ui-state]
            [web3.tracking.components :as tracking]
            [web3.env :as env]
            [web3.tracking.components :as tc]
            [web3.components.progress :as progress]
            [web3.persistence.current-property :as current-property]
            [web3.screens.loading :as loading]
            [web3.screens.root-public :as public]
            [web3.queries.root :as q-root]
            [web3.lazy :as lazy]
            [web3.components.classes :as classes]
            [web3.icons.shapes :as shapes]
            [web3.auth.api :as auth]))

;; ========== Root ==========

(v/defview powered-by-centriq
  []
  (let [{:keys [state]} (r/current-component)]
   [tc/a
    {:class "pad-left-3 pad-v-3 text-uppercase text-size-11 text-dark-gray visited-dark-gray hover-text-blue absolute left-0 bottom-0"
     :href "https://myhome.homeserve.com/"
     :target "_blank"
     :rel "noopener"
     :on-mouse-enter #(swap! state assoc :hovered true)
     :on-mouse-leave #(swap! state assoc :hovered false)
     :style {:width "180px"}}
    [:div.flex.flex-wrap.items-center
     {:style {:max-width "160px"}}
     [:div.flex-auto.weight-semi-bold (i/to-do "Powered by HomeServe")]]]))

(v/defview main
  [{:keys [toolbar-min-height mode content]}]
  (let [full-screen? (= mode :mode/full-screen)]
    [:div.width-100p.bg-off-white.relative
     {:style {:overflow "hidden"
              :z-index (:root/main constants/z-index)
              :transition (if full-screen? "opacity ease 0.2s 0.2s, min-height ease 0.2s 0.2s, max-height ease 0.2s 0.2s"
                                           "opacity ease 0.2s, min-height ease 0.2s")
              :opacity (if full-screen? 0 1)
              :max-height (when full-screen? 0)
              ;; known issue:
              ;; if we use dynamic minimum height (based on window listener),
              ;; safari flickers the scroll bar visible/invisible as we shrink the window.
              :min-height (if full-screen? 0
                                           (- (:window/inner-height @ui-state/ui-globals)
                                              toolbar-min-height))}}
     content
     [powered-by-centriq]]))

(defn set-current-property!
  "The `layout` view functions as a state machine, sets current-property whenever it changes."
  [id]
  (db/dispatch [:assoc :current-property-id id]))

(v/defview layout
  "Displays app navigation, and matches current route to handler."
  {:get-initial-state
   (fn [this]
     (select-keys (:props this) [:view/transition]))
   :component-will-receive-props
   (fn [this [_ next-props :as next-argv]]
     (let [{state :state
            {property :current-property-id :as props} :props} this
           {next-transition :view/transition
            next-property :current-property-id} next-props]
       (cond (not= (:view/transition props) next-transition)
             (swap! state assoc :view/transition next-transition)
             (and property (not= property next-property))
             (swap! state assoc :view/transition :bounce-100%))
       ((:will-receive-props ui-state/manage-scroll) :layout this next-argv)))
   :component-did-mount
   (fn [{:keys [state props]}]
     (when (:transition @state)
       (swap! state dissoc :view/transition))
     (some-> (:current-property-id props)
             (set-current-property!)))
   :component-did-update
   (fn [{:as this
         :keys [state props]}]
     (let [{:keys [view/transition]} @state]
       (when transition
         (js/setTimeout #(swap! state dissoc :view/transition) 200))
       (some-> (:current-property-id props)
               (set-current-property!))
       ((:did-update ui-state/manage-scroll) :layout this)))}
  [{:keys [view/mode
           view/nav-content
           view/body-content
           load-complete?
           active?]}]
  (let [{:keys [view/transition view/prev-props]} (r/state (r/current-component))
        toolbar-min-height 80
        toolbar-max-height 450
        mode (case transition
               (:from-100%
                :bounce-100%) :mode/full-screen
               mode)]
    [:div.root.bg-off-white
     {:style (if active?
               {:min-height "100vh"}
               (:inactive-styles ui-state/manage-scroll))}

     ;; ========== Fixed Views ==========

     [top-nav/toolbar (merge {:min-height toolbar-min-height
                              :max-height toolbar-max-height
                              :mode mode
                              :navigation-visible? load-complete?
                              :transition transition
                              :view/nav-content (if transition (:view/nav-content prev-props)
                                                               nav-content)})]

     [snackbar/snackbar {:key "root-snackbar"
                         :snacks (db/get ::snackbar/snacks)}]

     (when load-complete?
       [intercom/badge])

     (when (#{"files" "home" "group"}
            (some-> (db/get-in [:db/routers :router/root :handler])
                    (utils/keyword-root)))
       [top-nav/fixed-button-add
        {:min-height toolbar-min-height
         :max-height toolbar-max-height
         :mode mode}])
     [main {:toolbar-min-height toolbar-min-height
            :mode mode
            :content (if transition (:view/body-content prev-props)
                                    body-content)}]]))

(defn error-view
  "Render messages for map of the form {<error-key>, <error-data>}"
  [{:keys [errors]}]
  [:div.flex.items-center.justify-center.height-100vh
   (let [[{:keys [message]} & other-errors] (vals errors)]
     [alerts/alert-view
      {:text-primary (i/get :errors/unknown)
       :text-secondary message
       :buttons [{:label "Home"
                  :href "/"}]}])])

(defn ensure-user-has-property!
  "If root query is loaded, but user has no property,
   start the welcome flow."
  [{:as root-query
    :keys [graphql/loading?
           graphql/error
           me]}]
  (js/setTimeout
   #(when (and (not loading?)
               (not error)
               (empty? (:propertyRights me))
               (not (db/get-in [:db/routers :router/form-modal])))
      (routing/nav! (routing/path-for {:router/form-modal [:welcome/start]})))
   50))

(db/defquery body-classes []
  (let [{:keys [full-modal?
                scroll-lock?
                router/drawer
                router/form-modal
                router/content-modal
                alert?
                mobile?]} (ui-state/modal-state)
        intercom? (db/get-in [:db/intercom :active?])]
    {:mobile-active mobile?
     :modal-scroll-lock scroll-lock?
     :modal-full full-modal?
     :modal-scroll-overheight (and (not mobile?)
                                   (not drawer)
                                   (or form-modal
                                       content-modal
                                       alert?)
                                   (boolean (utils/scrollable?)))
     :intercom-active intercom?}))

(defn nav-loading-screen []
  [:div.pad-h-4.margin-h-2 [progress/indeterminate {:color "#03839c"}]])

(v/defview authed-root
  "Gets route, passes to layout"
  {:component-will-unmount #(tracking/dissoc-from-context! ::current-property)
   :component-did-update #(let [p (current-property/get-property)]
                            (tracking/assoc-in-context! ::current-property p))}
  [{{:keys [view view/props route-params]
     :as root} :router/root
    :as routers}]
  (let [mode (or (:view/mode (:router/form-modal routers))
                 (:view/mode (:router/root routers))
                 (:view/mode (:router/content-modal routers))
                 :mode/nav-collapsed)

        active? (or (not (ui-state/mobile?))
                    (not (:any-modal? (ui-state/modal-state))))

        current-property (current-property/get-property)

        route-params (merge (select-keys root [:handler])
                            route-params
                            props
                            {:current-property current-property})
        {:as root-query
         :keys [graphql/loading? graphql/error]} (q-root/me)
        load-complete? (and (not error)
                            (not loading?))
        mode (if load-complete? mode :mode/full-screen)
        nav-view (v/class-get view :nav-view)]

    (when-let [me (:me root-query)]
      (intercom/update-user! me))

    (ensure-user-has-property! root-query)

    [:div
     (when load-complete?
       [public/modal-views {:modal/closable? true}])
     [layout (merge (db/get-in [:db/routers :router/root])
                    {:loading? loading?
                     :load-complete? load-complete?
                     :active? active?
                     :view view
                     :view/mode mode
                     :view/body-content (when view
                                          [view route-params])
                     :view/nav-content (cond error [error-view (assoc route-params :errors {:query error})]
                                             loading? [nav-loading-screen route-params]
                                             nav-view [nav-view (merge route-params
                                                                       {:active-modal? (:any-modal? (ui-state/modal-state))})])
                     :current-property-id (:id current-property)})]]))

;;TODO rename to include public/auth-routing
(v/defview try-root
  "Wraps root route in a catch-all error handler"
  {:component-did-catch (fn [error info]
                          (rf/dispatch-sync [:events/handle-error {:source :error/react-did-catch
                                                                   :message "An unknown error has occurred."
                                                                   :debug-log {:stack (.-componentStack info)}
                                                                   :severity :high
                                                                   :error error
                                                                   :db-path [:errors :router/root :error/react-did-catch]}]))}
  []
  (let [app-errors (db/get-in [:errors :router/root])
        {:as routers
         root-router :router/root} (db/get :db/routers)]
    [:div

     (cond app-errors [error-view {:errors app-errors}]
           (not root-router) [loading/loading-screen]
           (-> (:view root-router)
               (v/class-get :allow-public)) [public/root routers]
           (not (auth/loaded?)) [loading/loading-screen]
           :else [authed-root routers])

     ;; utility views
     [classes/set-classes js/document.body body-classes]
     [hint/render-hints]
     (when env/devtools?
       [(lazy/wrap-view (lazy/loadable web3.screens.dev-samples/link))])]))


